import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import { getReasonsEndPoint } from '../../../../common/api.endPoints';
import { Decision } from '../../../../common/constants/decision';
import { Reason, ReasonsTransform } from '../../../../common/constants/reasons';
import { HttpService } from '../../../../common/http.service';
import { fetchPostponeConditions } from '../../../../common/metadata.slice';
import { SnackBarType } from '../../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../../layout/SnackbarContext';
import { IDecision } from '../../interfaces/IDecision';
import { EditPostpone } from './EditPostpone';
import { ReviewPostpone } from './ReviewPostpone';
import { PostponeFormData } from './usePostponeForm';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const PostponeDrawer = ({
  isOpen,
  setIsOpen,
  applicationId,
  editedDecision,
  onDecisionChanged,
  isEditLeadingToCLR,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  applicationId: string;
  editedDecision?: IDecision;
  onDecisionChanged?: (decision: Decision) => void;
  isEditLeadingToCLR?: boolean;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { openSnack } = useContext(SnackbarContext);

  const {
    rateClass,
    tobaccoClass,
    coverage,
    term,
  }: {
    rateClass: string;
    tobaccoClass: string;
    coverage: number;
    term: number;
  } = useSelector(
    (state: RootState) => ({
      product: state.application.product,
      rateClass: state.application.healthClass,
      tobaccoClass: state.application.tobaccoClass,
      coverage: state.application.coverage,
      term: state.application.term,
    }),
    shallowEqual,
  );
  const {
    createDecisionFailed,
    createDecisionError,
    createDecisionErrorType,
  }: {
    createDecisionFailed: boolean;
    createDecisionError: string;
    createDecisionErrorType: string;
  } = useSelector(
    (state: RootState) => ({
      createDecisionFailed: state.decision.createDecisionFailed,
      createDecisionError: state.decision.createDecisionError,
      createDecisionErrorType: state.decision.createDecisionErrorType,
    }),
    shallowEqual,
  );

  const postponeConditionChoices = useSelector(
    ({ metadata }) => metadata.postponeConditions,
  );

  const [formData, setFormData] = useState<PostponeFormData | null>(null);
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [isReview, setIsReview] = useState(false);

  useEffect(() => {
    (async () => {
      const response = await HttpService.getInstance().get(
        getReasonsEndPoint(applicationId),
      );
      if (response && !response.error) {
        const orderReason: [] = response.parsedBody;
        const orderReasonMap: Reason[] = orderReason.map(item => {
          return ReasonsTransform.getReason(item) as Reason;
        });
        setReasons(orderReasonMap as Reason[]);
      }
    })();
  }, []);

  useEffect(() => {
    if (isOpen) return;
    setFormData(null);
  }, [isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      (!postponeConditionChoices.items ||
        postponeConditionChoices.items.length === 0) &&
      !postponeConditionChoices.loading
    ) {
      dispatch(fetchPostponeConditions());
    }
  }, [isOpen]);

  useEffect(() => {
    if (!createDecisionFailed) return;

    openSnack({
      id: uuidv4(),
      type: createDecisionErrorType as SnackBarType,
      message: createDecisionError,
    });
  }, [createDecisionFailed, createDecisionError, createDecisionErrorType]);

  const onEditSuccess = useCallback((data: PostponeFormData) => {
    setFormData(data);
    setIsReview(true);
  }, []);

  return (
    <>
      {isReview && formData && (
        <ReviewPostpone
          applicationId={applicationId}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          coverage={coverage}
          term={term}
          rateClass={rateClass}
          tobaccoClass={tobaccoClass}
          formData={formData}
          editedDecision={editedDecision}
          onBack={() => setIsReview(false)}
          isEditLeadingToCLR={isEditLeadingToCLR}
        />
      )}
      {!isReview && (
        <EditPostpone
          initialFormData={formData}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          coverage={coverage}
          term={term}
          rateClass={rateClass}
          tobaccoClass={tobaccoClass}
          reasons={reasons}
          postponeConditions={postponeConditionChoices.items}
          isUpdate={!!editedDecision}
          onSuccess={onEditSuccess}
          onDecisionChanged={onDecisionChanged}
          editedDecision={editedDecision}
        />
      )}
    </>
  );
};
